import classNames from 'classnames';
import React, {useCallback} from 'react';

import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {SubscriptionPlanProductType} from '@udemy/graphql';
import {useMatchMedia} from '@udemy/hooks';
import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {Icon, type IconProps} from '@udemy/icons';
import {BlockList} from '@udemy/react-core-components';
import {
    ContentArea,
    ContentGrid,
    ContentGridItem,
    OnePaneLayoutFullWidthSectionContent,
} from '@udemy/react-layout-components';
import {
    GrowWithGoogleBannerPlanStats,
    SubscriptionOfferPresented,
    GwgSubscriptionCTA,
} from '@udemy/subscription-browse';
import {
    SubscriptionPlan,
    useConsumerSubscriptionPlan,
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
    useActiveSubscriptions,
} from '@udemy/subscription-graphql';

import {ClientSideVideoPlayerByAssetId} from 'src/components/client-side-video-player';
import {GwgPriceDisplay} from 'src/components/gwg-price-display';

import styles from './explore-plan-section.module.less';

export interface ExplorePlanSectionProps {
    planStats?: GrowWithGoogleBannerPlanStats;
}

type FeatureItem = {
    icon: IconProps['glyph'];
    content: React.ReactNode;
};

const EXPLORE_PLAN_UI_REGION = 'google-ai-plan-explore';

export const ExplorePlanSection = ({planStats}: ExplorePlanSectionProps) => {
    const {gettext, interpolate} = useI18n();
    const isLgMin = useMatchMedia('lg-min');
    const personalPlanContext = useConsumerSubscriptionPlan();
    const monthlyPlanOption =
        personalPlanContext && 'monthlyPlanOption' in personalPlanContext
            ? personalPlanContext.monthlyPlanOption
            : undefined;
    const subscriptionPlan = personalPlanContext as SubscriptionPlan | undefined;

    const trackOfferPresented = useCallback(() => {
        if (!subscriptionPlan || !monthlyPlanOption) {
            return;
        }
        Tracker.publishEvent(
            new SubscriptionOfferPresented({
                subscriptionPlan,
                productType: SubscriptionPlanProductType.Consumersubscription,
                countryCode: monthlyPlanOption?.listPrice?.currency ?? 'USD',
                uiRegion: EXPLORE_PLAN_UI_REGION,
            }),
        );
    }, [subscriptionPlan, monthlyPlanOption]);

    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const growWithGooglePlan = useConsumerSubscriptionGwGPlan();
    const activeSubscription = useActiveSubscriptions()[0];

    const features: FeatureItem[] = [
        {
            icon: 'video-group',
            content: (
                <LocalizedHtml
                    html={interpolate(
                        gettext('<span class="num">%(count)s</span> career learning paths'),
                        {count: planStats?.pathCount ?? 0},
                        true,
                    )}
                    interpolate={{
                        num: <span className={classNames(styles['feature-num'])} />,
                    }}
                />
            ),
        },
        {
            icon: 'labs',
            content: (
                <LocalizedHtml
                    html={interpolate(
                        gettext(
                            '<span class="num">%(count)s+</span> activities for real-world practice',
                        ),
                        {count: planStats?.labsCount ?? 0},
                        true,
                    )}
                    interpolate={{
                        num: <span className={classNames(styles['feature-num'])} />,
                    }}
                />
            ),
        },
        {
            icon: 'medal',
            content: (
                <LocalizedHtml
                    html={gettext(
                        'Industry-recognized credentials from <span class="google">Google</span>',
                    )}
                    interpolate={{google: <span className="ud-text-bold" />}}
                />
            ),
        },
        {
            icon: 'expand-plus',
            content: (() => {
                const template = gettext(
                    'Access to <span class="google">Google AI Pro</span> for 3 months at no cost <div><a class="termsApplyLink">%(termsApplyText)s</a></div>',
                );
                const htmlWithInterpolatedText = interpolate(
                    template,
                    {termsApplyText: gettext('Terms apply')},
                    true,
                );
                return (
                    <LocalizedHtml
                        html={htmlWithInterpolatedText}
                        interpolate={{
                            google: <span className="ud-text-bold" />,
                            termsApplyLink: (
                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                <a
                                    href="https://www.udemy.com/terms/learn-with-google/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={'ud-link-underline ud-text-sm'}
                                />
                            ),
                        }}
                    />
                );
            })(),
        },
    ];

    return (
        <TrackImpression trackFunc={trackOfferPresented}>
            <div>
                <OnePaneLayoutFullWidthSectionContent
                    aria-labelledby="explore-plan-section"
                    className={styles.container}
                >
                    <div className={styles.header}>
                        <h2
                            id="explore-plan-heading"
                            className={classNames({
                                'ud-heading-xxxl': isLgMin,
                                'ud-heading-xxl': !isLgMin,
                            })}
                        >
                            {gettext('Learn directly from Google experts')}
                        </h2>
                        <div className={classNames('ud-text-lg', styles['description'])}>
                            {gettext(
                                'Gain foundational AI skills and prompting techniques for high-quality results, learn to automate routine tasks, and sharpen your competitive edge in any field. Plus, you can earn a Google AI Professional Certificate to show your fluency.',
                            )}
                        </div>
                    </div>

                    <ContentArea>
                        <ContentGrid className={styles['content-grid']}>
                            <ContentGridItem
                                defaultSpan={12}
                                lgSpan={7}
                                className={styles['plan-video']}
                            >
                                <ClientSideVideoPlayerByAssetId
                                    assetId="71138421"
                                    playerId={'gwg-plan-overview'}
                                    trackingTag={'gwg-plan-overview'}
                                />
                            </ContentGridItem>
                            <ContentGridItem
                                defaultSpan={12}
                                lgSpan={5}
                                className={styles['plan-description']}
                            >
                                <div className={styles['plan-description-content']}>
                                    <h3 className="ud-heading-xl">
                                        {gettext('Build essential skills with AI')}
                                    </h3>
                                    <BlockList size="large">
                                        {features.map((feature, index) => (
                                            <BlockList.Item key={`${feature.icon}-${index}`}>
                                                <div className={styles['feature-item']}>
                                                    <Icon
                                                        glyph={feature.icon}
                                                        size="medium"
                                                        label={false}
                                                    />
                                                    <span>{feature.content}</span>
                                                </div>
                                            </BlockList.Item>
                                        ))}
                                    </BlockList>
                                </div>

                                <div className={styles['plan-description-cta-area']}>
                                    <GwgSubscriptionCTA
                                        plan={growWithGooglePlan}
                                        offerCtaStyle="primary"
                                        combinedGwgPlan={combinedGwgPlan}
                                        showSubtitle={false}
                                        subtitleClassName={classNames(
                                            styles['price-text'],
                                            'ud-text-sm',
                                        )}
                                        uiRegion="google-ai-plan-explore"
                                        size={'large'}
                                        currentlySubscribedPlan={activeSubscription}
                                    />
                                    <span className="ud-text-xs">
                                        <GwgPriceDisplay />
                                    </span>
                                </div>
                            </ContentGridItem>
                        </ContentGrid>
                    </ContentArea>
                </OnePaneLayoutFullWidthSectionContent>
            </div>
        </TrackImpression>
    );
};
